/**
 * Calendar utility functions for date handling and calendar operations
 */

export interface CalendarDate {
  year: number;
  month: number;
  day: number;
  date: Date;
}

/**
 * Get the first day of the month
 */
export function getFirstDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/**
 * Get the last day of the month
 */
export function getLastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

/**
 * Get the first day of the week for a given date (Sunday = 0)
 */
export function getFirstDayOfWeek(date: Date): Date {
  const day = date.getDay();
  const diff = date.getDate() - day;
  return new Date(date.setDate(diff));
}

/**
 * Get all days in a month as a calendar grid (including previous/next month days)
 */
export function getCalendarDays(year: number, month: number): CalendarDate[] {
  const firstDay = getFirstDayOfMonth(year, month);
  const lastDay = getLastDayOfMonth(year, month);
  const firstDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  
  const days: CalendarDate[] = [];
  
  // Add previous month's trailing days
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthLastDay = getLastDayOfMonth(prevYear, prevMonth).getDate();
  
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({
      year: prevYear,
      month: prevMonth,
      day: prevMonthLastDay - i,
      date: new Date(prevYear, prevMonth, prevMonthLastDay - i),
    });
  }
  
  // Add current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({
      year,
      month,
      day,
      date: new Date(year, month, day),
    });
  }
  
  // Add next month's leading days to fill the grid (35 days = 5 weeks)
  const remainingDays = 35 - days.length;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  
  for (let day = 1; day <= remainingDays; day++) {
    days.push({
      year: nextYear,
      month: nextMonth,
      day,
      date: new Date(nextYear, nextMonth, day),
    });
  }
  
  return days;
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  // Normalize both dates to midnight for accurate comparison
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1.getTime() === d2.getTime();
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  // Normalize both dates to midnight for accurate comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateToCheck = new Date(date);
  dateToCheck.setHours(0, 0, 0, 0);
  return dateToCheck.getTime() === today.getTime();
}

/**
 * Check if a date is in the current month
 */
export function isCurrentMonth(date: Date, year: number, month: number): boolean {
  return date.getFullYear() === year && date.getMonth() === month;
}

/**
 * Format date to readable string
 * 
 * TIMEZONE: Always displays in user's local timezone (via toLocaleDateString)
 * Note: For consistency, consider using formatDate from utils/time.ts instead
 */
export function formatDate(date: Date, format: 'short' | 'long' | 'month-year' = 'short'): string {
  let options: Intl.DateTimeFormatOptions;
  
  if (format === 'short') {
    options = { month: 'short', day: 'numeric', year: 'numeric' };
  } else if (format === 'long') {
    options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
  } else {
    options = { month: 'long', year: 'numeric' };
  }
  
  // toLocaleDateString automatically uses local timezone
  return date.toLocaleDateString('en-US', options);
}

/**
 * Get week number for a date
 */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Get all days in a week
 */
export function getWeekDays(date: Date): CalendarDate[] {
  // Don't mutate the input date - create a copy
  const dateCopy = new Date(date);
  const day = dateCopy.getDay();
  const diff = dateCopy.getDate() - day;
  const weekStart = new Date(dateCopy);
  weekStart.setDate(diff);
  weekStart.setHours(0, 0, 0, 0); // Normalize to start of day
  
  const days: CalendarDate[] = [];
  for (let i = 0; i < 7; i++) {
    const currentDate = new Date(weekStart);
    currentDate.setDate(weekStart.getDate() + i);
    currentDate.setHours(0, 0, 0, 0); // Normalize to start of day
    days.push({
      year: currentDate.getFullYear(),
      month: currentDate.getMonth(),
      day: currentDate.getDate(),
      date: currentDate,
    });
  }
  
  return days;
}

/**
 * Add months to a date
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if an event spans multiple days
 */
export function isMultiDayEvent(startTime: Date, endTime: Date): boolean {
  const start = startTime instanceof Date ? startTime : new Date(startTime);
  const end = endTime instanceof Date ? endTime : new Date(endTime);
  return !isSameDay(start, end);
}

/**
 * Check if an event is an all-day event (no specific time)
 * Checks the isAllDay flag first, then falls back to time-based detection (00:00 to 23:59)
 */
export function isAllDayEvent(startTime: Date, endTime: Date, isAllDayFlag?: boolean): boolean {
  // If isAllDay flag is explicitly set, use it
  if (isAllDayFlag === true) {
    return true;
  }
  // Fall back to time-based detection for backward compatibility
  const start = startTime instanceof Date ? startTime : new Date(startTime);
  const end = endTime instanceof Date ? endTime : new Date(endTime);
  const startHour = start.getHours();
  const startMin = start.getMinutes();
  const endHour = end.getHours();
  const endMin = end.getMinutes();
  return (startHour === 0 && startMin === 0) && (endHour === 23 && endMin === 59);
}

/**
 * Check if an event has passed (ended before current time)
 * Handles both Date objects and ISO strings
 * For all-day events, checks if the event date has passed
 */
export function isEventPast(event: { endTime: Date | string; isAllDay?: boolean; isImportantDate?: boolean }): boolean {
  const now = new Date();
  const endTime = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
  
  // For all-day events or important dates, check if the end date has passed (compare dates, not times)
  if (event.isAllDay || event.isImportantDate) {
    const endDate = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return endDate.getTime() < today.getTime();
  }
  
  // For timed events, check if end time has passed
  return endTime.getTime() < now.getTime();
}


/**
 * Convert hex color to rgba with opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(59, 130, 246, ${opacity})`; // Default blue
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Get event card style based on members (Proposal 6: Left Border - Vertical Gradient)
 * Returns a style object with clean background and left border gradient
 */
export function getEventCardStyle(members: Array<{ memberColor?: string }>, isDarkMode: boolean): { background?: string; borderLeft?: string; leftBarGradient?: string; leftBarWidth?: string } {
  // Clean background - no gradient, fully opaque to prevent any color bleeding through
  const defaultBg = isDarkMode 
    ? 'rgba(31, 41, 55, 1)' // Fully opaque dark gray
    : 'rgba(255, 254, 251, 1)'; // Peachy off-white (#fffefb) instead of pure white
  
  if (!members || members.length === 0) {
    // Default fallback - use same div approach for consistency, use neutral gray instead of teal
    return {
      background: defaultBg,
      leftBarGradient: isDarkMode ? 'rgba(107, 114, 128, 0.8)' : 'rgba(156, 163, 175, 0.8)', // Neutral gray instead of teal
      leftBarWidth: '4px',
    };
  }

  const firstColor = members[0].memberColor || '#3b82f6';
  
  if (members.length === 1) {
    // Single member: use same div approach for consistency
    return {
      background: defaultBg,
      leftBarGradient: firstColor, // Solid color, not a gradient
      leftBarWidth: '4px',
    };
  } else {
    // Multiple members: vertical gradient with all colors
    const colors = members.map(m => m.memberColor || '#3b82f6');
    const gradientStops = colors.map((color, i) => 
      `${color} ${(i / (colors.length - 1)) * 100}%`
    ).join(', ');
    return {
      background: defaultBg,
      leftBarGradient: `linear-gradient(to bottom, ${gradientStops})`,
      leftBarWidth: '4px',
    };
  }
}

/**
 * Debug utility: Check the actual rendered color of event text
 * Call this from browser console: window.checkEventTextColors()
 */
export function checkEventTextColors(): void {
  if (typeof window === 'undefined') {
    console.log('This function must be run in the browser');
    return;
  }

  // Find event cards in different views
  const eventCards = document.querySelectorAll('[class*="text-gray-900"], [class*="text-gray-100"]');
  
  if (eventCards.length === 0) {
    console.log('No event cards found. Make sure you are on a calendar page.');
    return;
  }

  console.log('=== Event Text Color Analysis ===');
  console.log(`Found ${eventCards.length} event card(s)`);
  
  const colors = new Set<string>();
  const colorDetails: Array<{ element: Element; computedColor: string; classes: string }> = [];

  eventCards.forEach((card, index) => {
    const computedStyle = window.getComputedStyle(card);
    const computedColor = computedStyle.color;
    const classes = card.className;
    
    colors.add(computedColor);
    colorDetails.push({ element: card, computedColor, classes });

    if (index < 5) { // Show first 5 for brevity
      console.log(`\nEvent Card ${index + 1}:`);
      console.log(`  Computed Color: ${computedColor}`);
      console.log(`  RGB: ${computedColor}`);
      console.log(`  Classes: ${classes.substring(0, 100)}...`);
    }
  });

  console.log(`\n=== Summary ===`);
  console.log(`Unique colors found: ${colors.size}`);
  colors.forEach(color => {
    console.log(`  - ${color}`);
  });

  // Check if dark mode is active
  const htmlElement = document.documentElement;
  const isDarkMode = htmlElement.classList.contains('dark') || 
                     htmlElement.getAttribute('data-theme') === 'dark' ||
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  console.log(`\nDark mode detected: ${isDarkMode}`);
  console.log('\nTo use this function again, call: window.checkEventTextColors()');
  
  return colorDetails as any;
}

// Make it available globally in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).checkEventTextColors = checkEventTextColors;
}
